import "./index.css";
import React from "react";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderItemRow from "./OrderItemRow";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopType from "@/enum/ShopType";
import { observer } from "mobx-react-lite";
import OrderBasicInfo from "./OrderBasicInfo";
import classNames from "classnames";

interface OrderItemRowsProps {
    order: OrderDTO;
    mergerOrder?: OrderDTO;
    shopType?: ShopType;
    isSupplierShop?: boolean;
}

const OrderItemRows: React.FC<OrderItemRowsProps> = observer((props) => {
    const { order, mergerOrder, shopType, isSupplierShop } = props;

    const orderItems = order.orderItems || [];

    return (
        <>
            {orderItems.map((orderItem, index) => {
                return (
                    <OrderItemRow
                        key={`${order.id}_${orderItem.skuInfoId}_${index}`}
                        order={order}
                        orderItem={orderItem}
                        mergerOrder={mergerOrder}
                        shopType={shopType}
                        isSupplierShop={isSupplierShop}
                    />
                );
            })}
        </>
    );
});

interface TableBodyProps {
    order: OrderDTO;
    isShowRemarks?: boolean;
    shopType?: ShopType;
    mergerOrder?: OrderDTO;
    isSupplierShop?: boolean;
}

const TableBody: React.FC<TableBodyProps> = observer(function (props) {
    const { order, mergerOrder, isShowRemarks, shopType, isSupplierShop } = props;

    const children: JSX.Element[] = [];

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        order.childOrders.forEach((childOrder) => {
            const subChildren = (
                <TableBody
                    key={`table_body_${order.id}_${childOrder.id}`}
                    order={childOrder}
                    isShowRemarks={true}
                    mergerOrder={order}
                    shopType={shopType}
                />
            );

            children.push(subChildren);
        });
    } else {
        children.push(
            <OrderBasicInfo
                key={`table_body_basic_info_${order.id}`}
                order={order}
                isShowRemarks={isShowRemarks}
                shopType={shopType}
            />,
        );

        children.push(
            <OrderItemRows
                key={`table_body_order_item_${order.id}`}
                order={order}
                mergerOrder={mergerOrder}
                shopType={shopType}
                isSupplierShop={isSupplierShop}
            />,
        );
    }

    return <>{children}</>;
});

interface OrderDetailsProps {
    order: OrderDTO;

    isSupplierShop: boolean;

    shopType: ShopType;

    colSpan?: number;
}

const OrderDetails: React.FC<OrderDetailsProps> = observer((props) => {
    const { order, isSupplierShop, shopType, colSpan } = props;

    return (
        <tr
            className={classNames("order-details-row", {
                "order-row-wired-data": !!order._weirdInfo,
            })}
        >
            <td colSpan={colSpan} className="order-details">
                <table className="table order-item-table">
                    <colgroup>
                        <col width="20" />
                        <col width="60" />
                        <col width="500" />

                        {!isSupplierShop ? <col width="300" /> : <col width="80" />}

                        {isSupplierShop ? <col /> : null}
                    </colgroup>
                    <tbody>
                        <TableBody
                            order={order}
                            isShowRemarks={false}
                            shopType={shopType}
                            isSupplierShop={isSupplierShop}
                        />
                    </tbody>
                </table>
            </td>
        </tr>
    );
});

export default OrderDetails;
